import { Group, Tooltip } from '@mantine/core';
import { Button, Icon, Text } from '@repo/foundations';
import { DataTable } from '@repo/mantine-datatable';
import { every, upperFirst } from 'lodash-es';
import type { MouseEventHandler } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import {
	type IDatabuilderJob,
	type IIntegration,
	useDatabuilderJobList,
	useUpdateDatabuilderJob,
} from '../../../../../api';
import { forceFetchJobs } from '../../../../../api/hooks/databuilderJob/usePollDatabuilderJob';
import { DatabuilderJobType, SyncType } from '../../../../../interfaces';
import {
	formatEntityDateTime,
	formatShortTimeDifference,
} from '../../../../../utils/time';
import { TableV2Loader } from '../../../../TableV2';
import { DEFAULT_MAX_RECORD_SIZE } from '../../../../TableV2/constants';
import {
	FOOTER_HEIGHT,
	ROW_HEIGHT,
	useTableStyles,
} from '../../../../TableV2/TableV2.styles';
import { ExtractionDetail } from '../../../IntegrationExtraction/ExtractionDetail';
import {
	getExtractionBadgeStyles,
	getExtractionLabel,
	getExtractionStatus,
} from '../../../IntegrationExtraction/utils';
import useIntegrationExtraction from '../../../IntegrationSelectionPage/useIntegrationExtraction';
import { SyncJobLiveDuration } from './SyncJobLiveDuration';

const RECORDS_PER_PAGE = 10;

interface SyncTableProps {
	integration: IIntegration;
	type: SyncType;
}

export const SyncTable = memo(({ integration, type }: SyncTableProps) => {
	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: true,
		noBorder: true,
		disableSelectedDisplay: true,
		withHover: true,
	});

	const [page, setPage] = useState(1);

	const {
		extractionDetailJob,
		handleCloseExtractionDetail,
		handleOpenActiveExtraction,
		stopPolling,
	} = useIntegrationExtraction({
		id: integration.id,
		type,
		page,
	});

	const listFilter = useMemo(() => {
		const jobTypeFilter =
			type === SyncType.PULL
				? [
						DatabuilderJobType.SCHEMA,
						DatabuilderJobType.DUMMY,
						DatabuilderJobType.METADATA,
					]
				: [DatabuilderJobType.PUSH];
		return {
			integration_id: integration.id,
			type__in: jobTypeFilter.join(','),
		};
	}, [integration.id, type]);

	const { mutate: updateJob } = useUpdateDatabuilderJob({
		options: {
			onSuccess: () => forceFetchJobs(),
		},
	});

	const { data: databuilderJobs } = useDatabuilderJobList({
		filters: listFilter,
		page,
		options: {
			refetchInterval: (query) => {
				const { data } = query.state;
				if (
					!data?.results?.length ||
					every(
						data?.results,
						(job) =>
							job.failed || job.completed || job.terminated || job.timed_out
					)
				) {
					return false;
				}
				return 2000;
			},
		},
	});

	const handleTerminateJob = useCallback(
		(job: IDatabuilderJob): MouseEventHandler<HTMLButtonElement> =>
			(event) => {
				event.stopPropagation();
				stopPolling();
				updateJob({ data: { id: job.id, terminated: true } });
			},
		[stopPolling, updateJob]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Sync type',
				accessor: 'type',
				render: (record: IDatabuilderJob) => (
					<Text size="sm">{getExtractionLabel(record)}</Text>
				),
			},
			{
				title: 'Sync started',
				accessor: 'created_at',
				render: (record: IDatabuilderJob) => (
					<Tooltip label={formatEntityDateTime(record.created_at)}>
						<Text size="sm">
							{formatShortTimeDifference(record.created_at, true)}
						</Text>
					</Tooltip>
				),
			},
			{
				title: 'Time',
				accessor: 'duration',
				render: (record: IDatabuilderJob) => (
					<SyncJobLiveDuration job={record} />
				),
			},
			{
				title: 'Sync status',
				accessor: 'status',
				render: (record: IDatabuilderJob) => {
					const status = getExtractionStatus(record);
					const extractionStyles = getExtractionBadgeStyles(status);
					return (
						<Group spacing="sm" noWrap>
							<Group spacing="3xs" noWrap>
								<Icon
									size="md"
									name={extractionStyles.iconName}
									fillColor={extractionStyles.iconFillColor}
									color={extractionStyles.iconColor}
								/>
								<Text size="sm">{upperFirst(status)}</Text>
							</Group>
							{['Pending', 'Queued', 'Running'].includes(
								getExtractionStatus(record)
							) && <Button onClick={handleTerminateJob(record)}>Cancel</Button>}
						</Group>
					);
				},
			},
		],
		[handleTerminateJob]
	);

	return (
		<>
			<DataTable<IDatabuilderJob>
				borderRadius="md"
				withBorder
				paginationSize="md"
				maxHeight={44 + 2 + RECORDS_PER_PAGE * ROW_HEIGHT + FOOTER_HEIGHT}
				height={44 + 2 + RECORDS_PER_PAGE * ROW_HEIGHT + FOOTER_HEIGHT}
				classNames={tableClasses}
				noRecordsIcon={<Icon size="lg" name="search" />}
				noRecordsText={'No runs found'}
				onRowClick={handleOpenActiveExtraction}
				records={databuilderJobs?.results ?? []}
				columns={columns}
				page={page}
				onPageChange={setPage}
				recordsPerPage={RECORDS_PER_PAGE}
				totalRecords={databuilderJobs?.count ?? 0}
				paginationText={({ to, totalRecords }) => {
					const totalRecordsString =
						totalRecords >= DEFAULT_MAX_RECORD_SIZE
							? `${DEFAULT_MAX_RECORD_SIZE}+`
							: totalRecords.toLocaleString();
					return `Showing ${to.toLocaleString()} of ${totalRecordsString} syncs`;
				}}
				customLoader={<TableV2Loader />}
			/>
			{extractionDetailJob && (
				<ExtractionDetail
					integration={integration}
					job={extractionDetailJob}
					onClose={handleCloseExtractionDetail}
				/>
			)}
		</>
	);
});
